import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Input,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DocumentCategory,
  Organization,
  Category,
} from '../../../shared/sdk/models';
import Decimal from 'decimal.js/decimal.js';

import { OrganizationService } from '../../../organizations/organization.service';
import { notZeroValidator } from '../../../shared/directives/valid-payment.directive';
import { LanguageService } from '../../../shared/services/language.service';
import { take, switchMap, map, filter } from 'rxjs/operators';
import { CategoryService } from '../../../categories/category.service';
import { DocumentCategoryService } from '../document-category.service';
import { MyDpDateService } from '../../../shared/services/my-dp-date.service';
import * as moment from 'moment';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-document-category-add',
  templateUrl: './document-category-add.component.html',
  styleUrls: ['./document-category-add.component.css'],
})
export class DocumentCategoryAddComponent implements OnInit, AfterViewInit {
  @Input() documentCategory: DocumentCategory;
  @ViewChild('documentCategoryAmount', { static: true })
  documentCategoryAmount: ElementRef;
  categories: Category[];
  documentCategoryForm: FormGroup;
  documentCategoryTypes: any;
  loading = true;
  sent: boolean;
  private activeOrg: Organization;

  constructor(
    private activeModal: NgbActiveModal,
    private documentCategoryService: DocumentCategoryService,
    private languageService: LanguageService,
    private orgService: OrganizationService,
    private categoryService: CategoryService,
    private dateService: MyDpDateService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    // Form control
    this.documentCategoryForm = this.fb.group({
      id: [],
      categoryId: [null, Validators.required],
      documentId: [this.documentCategory.documentId, Validators.required],
      amount: [
        this.documentCategory.amount,
        [Validators.required, notZeroValidator(this.languageService)],
      ],
      date: [this.dateService.generate(moment())],
      custom: [],
    });

    if (!this.documentCategory.id) {
      this.documentCategory = new DocumentCategory(this.documentCategory);
    } else {
      this.documentCategory = new DocumentCategory(this.documentCategory);
      this.documentCategoryForm.patchValue(this.documentCategory);
    }

    this.documentCategoryForm.controls.date.patchValue(
      this.dateService.generate(moment(this.documentCategory.date)),
    );

    this.documentCategoryForm.controls.amount.patchValue(
      this.languageService.formatNumber(
        this.documentCategoryForm.controls.amount.value,
      ),
    );

    this.orgService.active
      .pipe(
        switchMap((org: Organization) => {
          this.activeOrg = org;
          return this.categoryService.get(this.activeOrg.id, {
            order: 'name ASC',
            deleted: true,
          });
        }),
        map(({ body }) => body),
        take(1),
      )
      .subscribe((categories: Category[]) => {
        const editing = !!this.documentCategory.id;
        const oldCatId = this.documentCategory.categoryId;

        // Filter out deleted expect if editing keep old one in case deleted
        this.categories = categories.filter((c) => {
          if (!editing) {
            return !c.deletedAt;
          } else {
            return !c.deletedAt || c.id === oldCatId;
          }
        });

        if (!editing && this.categories[0]) {
          this.documentCategoryForm.get('categoryId').patchValue(this.categories[0].id);
        }

        this.loading = false;
      });
  }

  ngAfterViewInit() {
    setTimeout(() => this.documentCategoryAmount.nativeElement.focus(), 100);
  }

  onClose() {
    this.activeModal.dismiss();
  }

  onSave() {
    this.sent = true;

    if (this.documentCategoryForm.valid) {
      this.loading = true;
      this.documentCategory = this.documentCategoryForm.value;

      this.documentCategory.amount = new Decimal(
        this.languageService.unformatNumber(this.documentCategory.amount),
      ).toNumber();

      this.documentCategory.date = this.dateService.parse(
        this.documentCategory.date,
      );

      this.documentCategoryService[
        !this.documentCategory.id ? 'create' : 'update'
      ](
        this.documentCategory.id || this.documentCategory.documentId,
        this.documentCategory,
      ).subscribe(
        (documentCategory: DocumentCategory) => {
          this.activeModal.close(documentCategory);
          this.loading = false;
        },
        () => (this.loading = false),
      );
    }
  }
}
